import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, MessageSquare } from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";

export default function MessageCustomerDialog({ open, onOpenChange, booking }) {
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("Message from JDSN Lawn Care");
  const [sms, setSms] = useState(true);
  const [email, setEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [variables, setVariables] = useState({});

  useEffect(() => {
    if (!open) return;
    setBody(""); setTemplateKey(""); setVariables({});
    api.get("/admin/message-templates").then(({ data }) => {
      setTemplates(data.templates || []);
    }).catch(() => setTemplates([]));
  }, [open]);

  const pickTemplate = (key) => {
    setTemplateKey(key);
    const tmpl = templates.find((t) => t.key === key);
    if (tmpl) setBody(tmpl.body);
  };

  const submit = async () => {
    const channels = [];
    if (sms) channels.push("sms");
    if (email) channels.push("email");
    if (channels.length === 0) return toast.error("Pick at least one channel");
    if (!body && !templateKey) return toast.error("Enter a message or pick a template");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.booking_id}/message`, {
        template_key: templateKey || null,
        variables: Object.keys(variables).length ? variables : null,
        custom_message: !templateKey ? body : null,
        subject,
        channels,
      });
      toast.success(`Sent · SMS ${data.sent.sms ? "✓" : "—"} · Email ${data.sent.email ? "✓" : "—"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSubmitting(false); }
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="message-customer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#047857]" /> Message · {booking.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="text-xs text-gray-500">{booking.service_name} · {booking.phone || "—"} · {booking.email || "—"}</div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600">Quick template</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="message-templates">
              {templates.map((t) => (
                <button key={t.key} type="button" onClick={() => pickTemplate(t.key)} data-testid={`msg-template-${t.key}`}
                  className={`rounded-xl border p-2.5 text-left text-[12px] ${templateKey === t.key ? "bg-emerald-50 border-emerald-300" : "bg-white border-gray-200"}`}>
                  <div className="font-extrabold capitalize">{t.key.replace(/_/g, " ")}</div>
                  <div className="text-gray-500 line-clamp-2">{t.body}</div>
                </button>
              ))}
            </div>
          </div>

          {templateKey && (
            <div className="grid grid-cols-2 gap-2" data-testid="message-template-vars">
              {/* Common variables — admin can override */}
              {["minutes", "next_date"].map((k) => (
                <div key={k}>
                  <Label className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{k}</Label>
                  <Input value={variables[k] || ""} onChange={(e) => setVariables({ ...variables, [k]: e.target.value })}
                    placeholder={k === "minutes" ? "15" : "Aug 14"} data-testid={`msg-var-${k}`} />
                </div>
              ))}
            </div>
          )}

          <div>
            <Label>Message</Label>
            <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Type or pick a template above"
              data-testid="message-body" />
          </div>

          <div>
            <Label>Email subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="message-subject" />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-bold">
              <input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} data-testid="message-channel-sms" /> SMS
            </label>
            <label className="flex items-center gap-2 text-sm font-bold">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} data-testid="message-channel-email" /> Email
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={submitting} data-testid="message-send-btn"
            className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
